import Flickity from 'flickity'
import { VisibilityElement } from '../custom-elements/visibility'
import { formatMoney } from './currency-formatter'
import { Cart as CartType, UnifiedItem } from './cart.types'

export class PWP {
  static PRODUCT_WITH_PURCHASE_ID = 'product-with-purchase'
  static PRODUCT_WITH_PURCHASE_SELECTOR = '[data-id="product-with-purchase"]'
  static PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY = 'pwp-display-history'
  static CAROUSEL_SELECTOR = '.flickity-element'
  static ADDED_ITEM_SELECTOR = '[data-pwp-added-item]'
  static SHIPPING_SELECTOR = '[data-pwp-shipping]'

  /**
   * Populates the "recently added to cart" block inside the PWP drawer with the
   * image, title and price of the line item that was just added to the cart.
   */
  static populateAddedItem(item?: Partial<UnifiedItem>): void {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase || !item) return

    const container = productWithPurchase.querySelector<HTMLElement>(PWP.ADDED_ITEM_SELECTOR)
    if (!container) return

    const image = container.querySelector<HTMLImageElement>('[data-pwp-added-image]')
    const title = container.querySelector<HTMLElement>('[data-pwp-added-title]')
    const price = container.querySelector<HTMLElement>('[data-pwp-added-price]')
    const variant = container.querySelector<HTMLElement>('[data-pwp-added-variant]')

    const imageUrl = item.featured_image?.url || item.image
    if (image && imageUrl) {
      image.src = imageUrl
      image.alt = item.featured_image?.alt || item.product_title || item.title || ''
    }
    if (title) {
      title.textContent = item.product_title || item.title || ''
    }
    if (variant) {
      const values = item.product_has_only_default_variant
        ? []
        : (item.options_with_values ?? []).map((option) => option.value).filter(Boolean)

      variant.replaceChildren()
      values.forEach((value, index) => {
        if (index > 0) {
          const separator = document.createElement('span')
          separator.className = 'text-neutral-medium'
          separator.textContent = ' | '
          variant.append(separator)
        }
        variant.append(value)
      })
      variant.classList.toggle('hidden', values.length === 0)
    }
    if (price) {
      const amount = item.final_price ?? item.price
      if (typeof amount === 'number') price.textContent = formatMoney(amount)
    }

    container.classList.remove('hidden')
    container.classList.add('flex')
  }

  /**
   * Updates the free shipping progress bar inside the PWP drawer against the latest cart total.
   */
  static updateShippingBar(cart?: CartType): void {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase || typeof cart?.total_price !== 'number') return

    const container = productWithPurchase.querySelector<HTMLElement>(PWP.SHIPPING_SELECTOR)
    if (!container) return

    const minimum = parseInt(container.dataset.freeShippingMinimum || '', 10)
    if (!minimum) return

    const remaining = minimum - cart.total_price
    const percentage = remaining <= 0 ? 100 : Math.floor((cart.total_price / minimum) * 100)

    container.dataset.reached = String(remaining <= 0)

    const remainingText = container.querySelector<HTMLElement>('[data-pwp-shipping-remaining]')
    if (remainingText) remainingText.textContent = formatMoney(Math.max(remaining, 0))

    const bar = container.querySelector<HTMLElement>('[data-pwp-shipping-bar]')
    if (bar) bar.style.setProperty('--shipping-bar-width', `${percentage}%`)
  }

  static showProductWithPurchase(addedItem?: Partial<UnifiedItem>, cart?: CartType): boolean {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)

    // If PWP element doesn't exist or there's no global PWP data, exit early
    if (!productWithPurchase || !window.globals?.metaobjects?.product_with_purchase) {
      return false
    }

    const pdpProductId = (productWithPurchase as HTMLElement).dataset.productId
    if (pdpProductId && String(addedItem?.product_id) !== pdpProductId) {
      return false
    }

    const pwpData = window.globals.metaobjects.product_with_purchase
    const retriggerDelay = pwpData.retrigger_delay

    // Get last display time from session storage
    let lastDisplayed: Date | null = null
    try {
      const storageData = localStorage.getItem(PWP.PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY)
      if (storageData) {
        lastDisplayed = new Date(storageData)
      }
    } catch (error) {
      console.error('Error parsing PWP display history:', error)
    }

    const now = new Date()

    // Check if we should show the PWP based on retrigger delay
    let shouldShow = true

    if (retriggerDelay > 0 && lastDisplayed) {
      // Calculate hours since last display
      const hoursSinceLastDisplay = Math.floor((now.valueOf() - lastDisplayed.valueOf()) / (1000 * 60 * 60))
      // Only show if enough hours have passed since last display
      shouldShow = hoursSinceLastDisplay >= retriggerDelay
    }

    if (shouldShow) {
      PWP.populateAddedItem(addedItem)
      PWP.updateShippingBar(cart)

      // Show the PWP
      VisibilityElement.dispatchShow(PWP.PRODUCT_WITH_PURCHASE_ID)

      // Update display history with current timestamp
      localStorage.setItem(PWP.PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY, now.toISOString())

      PWP.reloadFlickity()
      return true
    }

    return false
  }

  static reloadFlickity() {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase) return
    const carousel = productWithPurchase.querySelector(PWP.CAROUSEL_SELECTOR)
    if (carousel) {
      Flickity.data(carousel).resize()
    }
  }
}
